import { DndContextProps, MouseSensor, useSensor, useSensors } from '@dnd-kit/core';
import { HTMLProps, ReactNode, useMemo } from 'react';
import { overlayKey, pageKey, pageSizeKey, withArchiveKey } from '../../helpers/path';
import { useFlashElement } from '../../hooks/use-flash-element/use-flash-element';
import { useLang } from '../../hooks/use-lang';
import { useLocalStorageSubscribe } from '../../hooks/use-local-storage-subscribe';
import { useQueryParams } from '../../hooks/use-query-params';
import { PaginationType } from '../../types/pagination';
import { getKeysToHide, getSortOrder, getTableId } from './helpers';

interface OnEmptyProps {
  title?: string;
  text?: string;
}

export interface TableColumn {
  filter?: ReactNode;
  label?: ReactNode;
  maxWidth?: string | number;
  width?: string | number;
}

export interface TableProps extends Omit<HTMLProps<HTMLDivElement>, 'cols' | 'rows'> {
  cols?: Record<string, TableColumn>;
  rows?: Record<string, ReactNode | string>[];
  renderExpandedRowContent?: (row: Record<string, ReactNode | string>, index: number) => ReactNode;
  expandedRows?: string[];
  frontPagination?: boolean;
  pagination?: PaginationType;
  onEmpty?: OnEmptyProps;
  id?: string;
  columnSettings?: boolean;
  isLoading?: boolean;
  orderable?: boolean;
  onOrder?: (data: ReactNode[]) => void;
  header?: HTMLProps<HTMLDivElement>;
  pagePrefix?: string;
  expandedRowIds?: (string | number)[];
  renderHead?: boolean;
  withArchive?: boolean;
}

export const useTable = (props: TableProps) => {
  const {
    cols = {},
    rows = [],
    className,
    frontPagination,
    pagination,
    onEmpty,
    id,
    withArchive,
    columnSettings,
    isLoading,
    onOrder,
    pagePrefix,
    renderExpandedRowContent,
    expandedRowIds,
    renderHead = true,
    ...other
  } = props;
  const { t } = useLang();

  const tableId = getTableId(id);
  const keysToHide = getKeysToHide(tableId);
  const sortOrder = getSortOrder(tableId);
  const orderable = !!onOrder;
  const sensors = useSensors(useSensor(MouseSensor, {}));

  useLocalStorageSubscribe();

  const updatedCols = useMemo(() => {
    // hiding cols
    const freezed = { ...cols };
    keysToHide.forEach((key) => {
      delete freezed[key];
    });

    // sorting cols
    const rearranged: Record<string, TableColumn> = {};
    Object.keys(freezed)
      .map((key) => ({ columnId: key, ...freezed[key] }))
      .sort((a, b) => sortOrder.indexOf(a.columnId) - sortOrder.indexOf(b.columnId))
      .forEach((item) => {
        const { columnId, ...other } = item;
        rearranged[columnId] = other;
      });

    return rearranged;
  }, [cols, keysToHide, sortOrder]);

  const updatedRows = useMemo(() => {
    // hiding rows
    const freezed = rows.map((row) => ({ ...row }));
    keysToHide.forEach((key) => {
      freezed.forEach((row) => {
        delete row[key];
      });
    });

    // sorting rows
    const rearranged: Record<string, ReactNode | string>[] = [];
    freezed.forEach((row) => {
      const rearrangedRow: Record<string, ReactNode | string> = {};
      Object.keys(row)
        .sort((a, b) => sortOrder.indexOf(a) - sortOrder.indexOf(b))
        .forEach((key) => {
          rearrangedRow[key] = row[key];
        });
      rearranged.push(rearrangedRow);
    });

    return rearranged;
  }, [keysToHide, rows, sortOrder]);

  const { addQueryParams, getQueryParams } = useQueryParams();
  const params = getQueryParams();
  const withArchiveParam = !!params[withArchiveKey] && !!withArchive;
  const overlayId = params[overlayKey];
  const resultPageKey = pagePrefix ? pagePrefix + pageKey : pageKey;
  const resultPageSizeKey = pagePrefix ? pagePrefix + pageSizeKey : pageSizeKey;
  const page = Number(params[resultPageKey] || '1');
  const pageSize = Number(params[resultPageSizeKey] || '10');

  const elementId = overlayId?.split('-').pop();
  useFlashElement({
    isReady: !isLoading && !!rows.length,
    selector: `[data-table-row="${elementId}"]`,
  });

  // Удалить когда будет готов бэк
  const rowsWithPagination = useMemo(() => {
    if (!frontPagination || pagination) return updatedRows;
    const start = (page - 1) * pageSize;
    const end = page * pageSize;
    return updatedRows?.slice(start, end);
  }, [frontPagination, updatedRows, page, pageSize]);
  //

  const handleDragEnd: DndContextProps['onDragEnd'] = (event) => {
    const { active, over } = event;

    if (orderable && over && active.id !== over.id) {
      const oldIndex = rowsWithPagination.findIndex((item) => item.rowId === active.id);
      const newIndex = rowsWithPagination.findIndex((item) => item.rowId === over.id);
      const updatedData = Array.from(rowsWithPagination);
      const [movedItem] = updatedData.splice(oldIndex, 1);
      updatedData.splice(newIndex, 0, movedItem);
      onOrder(updatedData.map((item) => item.rowId));
    }
  };

  const toggleWithArchive = () => {
    addQueryParams({
      [withArchiveKey]: withArchiveParam ? undefined : 'true',
      [pageKey]: '1',
    });
  };

  return {
    cols: updatedCols,
    rows: updatedRows,
    className,
    frontPagination,
    pagination,
    onEmpty,
    other,
    t,
    rowsWithPagination,
    tableId,
    columnSettings,
    isLoading,
    sensors,
    orderable,
    handleDragEnd,
    renderExpandedRowContent,
    expandedRowIds,
    renderHead,
    withArchiveParam,
    toggleWithArchive,
  };
};
